<script lang="ts">
  // A stack member outside the section, on one line of the stack's rail: a
  // loaded one as a link with its author, review state and checks; one not
  // loaded by its number (linked) or position; one GitHub does not show
  // the viewer, or whose read needs SSO authorization, as a plain number,
  // saying which. Its main control is the
  // dashboard's keyboard stop `stop` (see dashFocus.ts).
  import type { DashRow } from '../state/dashboard.svelte';
  import type { MissingNode, PrNode } from '../logic/stacks';
  import { checkState, type CheckState } from '../logic/stackState';
  import { prPath } from '../router';
  import AuthorMark from './AuthorMark.svelte';

  interface Props {
    member: PrNode<DashRow> | MissingNode<DashRow>;
    /** The stack spans repositories: numbers name theirs. */
    crossRepo: boolean;
    /** Where it is instead (`not in this section`), for a member shown on its own row. */
    note: string | null;
    /** Its keyboard stop. */
    stop: string;
    /** Whether it takes the Tab key (the cursor is on it). */
    tabbable: boolean;
    /** A loaded member's review pill, from its row's review state (`rowReview`). */
    pill: 'approved' | 'changes' | null;
  }
  let { member, crossRepo, note, stop, tabbable, pill }: Props = $props();

  const CI_SHORT: Record<CheckState, string> = { passing: 'CI passing', failing: 'CI failing', pending: 'CI running', none: '' };
  const tabindex = $derived(tabbable ? 0 : -1);
  const label = (repo: string, number: number) => `${crossRepo ? repo : ''}#${number}`;
  const link = (repo: string, number: number) => {
    const [owner, name] = repo.split('/');
    return prPath(owner, name, number);
  };
</script>

{#if member.kind === 'pr'}
  {@const p = member.row.pr}
  {@const ci = checkState(p.commits.nodes[0]?.commit.statusCheckRollup?.state)}
  <a class="stack-aside-title" href={prPath(p.repository.owner.login, p.repository.name, p.number)} data-stop={stop} {tabindex}>{p.title}</a>
  <span class="mono">{label(p.repository.nameWithOwner, p.number)}</span>
  <AuthorMark login={p.author?.login} avatarUrl={p.author?.avatarUrl} size={14} />
  {#if p.isDraft}<span class="pill neutral">draft</span>{/if}
  {#if p.state === 'MERGED'}<span class="pill merged">merged</span>{:else if p.state === 'CLOSED'}<span class="pill bad">closed</span>{/if}
  {#if pill === 'approved'}<span class="pill">approved</span>{:else if pill === 'changes'}<span class="pill bad">changes requested</span
    >{:else if p.state === 'OPEN' && p.reviewDecision === 'REVIEW_REQUIRED'}<span>review required</span>{/if}
  {#if ci !== 'none'}<span class="stack-ci {ci}">{CI_SHORT[ci]}</span>{/if}
  {#if note}<span class="stack-aside-note">{note}</span>{/if}
{:else}
  {@const m = member}
  {@const count = m.to - m.from + 1}
  {@const name =
    m.first === null || m.last === null
      ? `position${count > 1 ? `s ${m.from}–${m.to}` : ` ${m.from}`}`
      : m.first === m.last
        ? label(m.repo, m.first)
        : `${label(m.repo, m.first)} … ${label(m.repo, m.last)}`}
  {#if m.first !== null && m.first === m.last && !m.hidden && !m.sso}
    <a class="mono" href={link(m.repo, m.first)} data-stop={stop} {tabindex}>{name}</a>
  {:else}
    <!-- Nothing to open: a link that cannot be followed, so the keys still stop on it and it reads as one. -->
    <span class="mono" role="link" aria-disabled="true" data-stop={stop} {tabindex}>{name}</span>
  {/if}
  {#if m.hidden}
    <span class="stack-aside-note">GitHub doesn't show {count > 1 ? 'them' : 'it'} to you</span>
  {:else if m.sso}
    <span class="stack-aside-note" title="Authorize your SSO session for the organization on GitHub, then refresh">needs SSO authorization</span>
  {:else}
    <span class="stack-aside-note">{count > 1 ? `${count} not loaded` : 'not loaded'}</span>
  {/if}
{/if}
